import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SEOHead from '../common/SEOHead';
import SafeIcon from '../common/SafeIcon';

const { FiBuilding, FiShoppingBag, FiBriefcase, FiTool, FiDollarSign, FiShield, FiCheck, FiTrendingUp, FiUsers } = FiIcons;

const CommercialServices = () => {
  const services = [
    {
      icon: FiBuilding,
      title: 'Office Buildings',
      description: 'Professional management for Calgary office spaces from downtown towers to suburban business parks.',
      features: ['Common area maintenance', 'Security management', 'HVAC systems', 'Parking management']
    },
    {
      icon: FiShoppingBag,
      title: 'Retail Properties',
      description: 'Specialized retail management for Calgary shopping centers, strip malls, and high street commercial units.',
      features: ['Tenant mix optimization', 'Foot traffic analysis', 'Promotional coordination', 'Maintenance scheduling']
    },
    {
      icon: FiBriefcase,
      title: 'Industrial Properties',
      description: 'Expert management for Calgary industrial parks, warehouses, and distribution centers.',
      features: ['Loading dock management', 'Safety compliance', 'Heavy equipment coordination', 'Environmental compliance']
    },
    {
      icon: FiBuilding,
      title: 'Mixed-Use Properties',
      description: 'Comprehensive management for Calgary properties combining residential, commercial, and retail spaces.',
      features: ['Multi-tenant coordination', 'Shared facility management', 'Zoning compliance', 'Revenue optimization']
    }
  ];

  const features = [
    {
      icon: FiUsers,
      title: 'Commercial Tenant Relations',
      description: 'Professional tenant management including lease negotiations, renewals, and dispute resolution for Calgary businesses.'
    },
    {
      icon: FiDollarSign,
      title: 'Financial Management',
      description: 'Comprehensive accounting, CAM reconciliation, and expense recovery for Calgary commercial properties.'
    },
    {
      icon: FiTool,
      title: 'Property Maintenance',
      description: 'Proactive maintenance programs ensuring optimal building performance and tenant satisfaction.'
    },
    {
      icon: FiShield,
      title: 'Regulatory Compliance',
      description: 'Full compliance with Calgary commercial building codes, safety regulations, and environmental standards.'
    },
    {
      icon: FiTrendingUp,
      title: 'Asset Value Enhancement',
      description: 'Strategic improvements and upgrades to increase Calgary property value and rental rates.'
    },
    {
      icon: FiBriefcase,
      title: 'Vendor Management',
      description: 'Coordination of all Calgary service providers including security, cleaning, and specialized contractors.'
    }
  ];

  const benefits = [
    'Increased Net Operating Income',
    'Reduced Vacancy Rates',
    'Enhanced Property Value',
    'Professional Tenant Relations',
    'Calgary Market Expertise',
    'Strategic Asset Management'
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Commercial Property Management Calgary",
    "description": "Professional commercial property management services in Calgary. Expert management of office buildings, retail spaces, industrial properties, and mixed-use developments.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "DGM Property Management",
      "telephone": "587-800-1215",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Calgary",
        "addressRegion": "Alberta",
        "addressCountry": "CA"
      }
    },
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "51.0447",
        "longitude": "-114.0719"
      },
      "geoRadius": "50000"
    },
    "serviceType": ["Commercial Property Management", "Office Building Management", "Retail Property Management", "Industrial Property Management"]
  };

  return (
    <>
      <SEOHead
        title="Commercial Property Management Calgary | Office & Retail Experts"
        description="DGM Property Management provides expert commercial property management in Calgary. Specializing in office buildings, retail spaces, industrial properties with 15+ years experience. Call 587-800-1215."
        keywords="commercial property management Calgary, Calgary office building management, retail property management Calgary, industrial property management Calgary, commercial real estate Calgary, Calgary property management company"
        canonical="/commercial"
        ogImage="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=630"
        schemaData={schemaData}
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-green-600 to-green-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Commercial Property Management Calgary
              </h1>
              <p className="text-xl text-green-100 max-w-3xl mx-auto">
                Calgary's premier commercial property management company. We optimize your commercial real estate investments through expert tenant relations, proactive maintenance, and strategic asset enhancement.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Calgary Commercial Properties We Manage
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Specialized management solutions for diverse commercial properties in Calgary's dynamic business landscape.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border border-gray-100"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="flex-shrink-0 w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={service.icon} className="w-8 h-8 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-4">
                        {service.description}
                      </p>
                      <div className="space-y-2">
                        {service.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center gap-2">
                            <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Calgary Commercial Management Excellence
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive commercial property services designed to optimize your Calgary real estate investment.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-lg"
                >
                  <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                    <SafeIcon icon={feature.icon} className="w-7 h-7 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-4xl font-bold text-gray-900 mb-8">
                  Why Choose DGM for Calgary Commercial Properties?
                </h2>
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <motion.div
                      key={benefit}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="flex items-center gap-3"
                    >
                      <SafeIcon icon={FiCheck} className="w-6 h-6 text-green-500 flex-shrink-0" />
                      <span className="text-lg text-gray-700">{benefit}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <div className="relative overflow-hidden rounded-2xl shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600"
                    alt="Modern commercial office building in Calgary managed by DGM Property Management"
                    className="w-full h-[500px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-green-900/50 to-transparent"></div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-green-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold mb-4">
                Optimize Your Calgary Commercial Property Investment
              </h2>
              <p className="text-xl text-green-100 max-w-2xl mx-auto">
                Partner with Calgary's commercial property management experts. Average NOI increase of 18% for our commercial clients.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="/#contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-green-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Get Free Property Valuation
                </motion.a>
                <motion.a
                  href="tel:587-800-1215"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-white hover:bg-white hover:text-green-600 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Call 587-800-1215
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CommercialServices;